import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import type {AddressComparisonFields} from "@mytour/shared";
import type {CSSProperties, ReactNode} from "react";
import {Component, Suspense, lazy} from "react";
import {Spinner} from "reactstrap";
import type {ManualPinPosition, MapCenterPrecision} from "../useAddressVerification.js";
import {
  RESOLVED_CARD_TINT,
  RESOLVED_ICON_TINT,
  STATUS_ICON_BASE_STYLE,
} from "./statusCardStyles.js";

/**
 * Import lazy (jamais statique) : `ManualPinMap` embarque Leaflet, et ce
 * composant est atteignable depuis le barrel `@mytour/ui/form` jusqu'à la
 * page de login — un import statique le mettrait sur le chemin critique de
 * `apps/web`.
 *
 * Repli sur échec de chunk (#1132/ex-#1003) : toute erreur remonte telle
 * quelle à `MapChunkErrorBoundary`, jamais de `window.location.reload()`
 * automatique — contrairement à `lazyWithReload` (`@mytour/platform`, chunks
 * de ROUTE), ce composant est monté à l'intérieur d'une modale de formulaire
 * (fiche client, adresse de départ) : un reload y détruit toute saisie non
 * enregistrée. Le boundary laisse le choix du rechargement à l'utilisateur.
 */
const ManualPinMap = lazy(() =>
  import("../ManualPinMap.js").then((module) => ({default: module.ManualPinMap}))
);

// `Suspense` ne rattrape pas un chunk qui échoue à charger — sans ce
// boundary de classe, l'erreur démonterait tout le formulaire (#929).
class MapChunkErrorBoundary extends Component<{children: ReactNode}, {hasError: boolean}> {
  state = {hasError: false};

  static getDerivedStateFromError() {
    return {hasError: true};
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{fontSize: "13px", color: "#dc3545", marginBottom: "8px"}}>
          <FontAwesomeIcon icon="triangle-exclamation" className="me-1" />
          La carte n'a pas pu se charger. Utilisez « Revenir à la vérification » ci-dessus, ou
          réessayez après avoir rechargé la page.
        </div>
      );
    }
    return this.props.children;
  }
}

type Props = {
  // Saisie courante — récap texte affiché au-dessus de la carte (les inputs sont masqués).
  fields: AddressComparisonFields;
  // Position du marqueur — le dernier drop de la session carte, sinon le centre figé à l'ouverture (#1011).
  position: ManualPinPosition;
  // Répétable, la carte reste ouverte (#1011) : la validation reste le bouton Continuer/Enregistrer de l'appelant.
  onDragEnd: (position: ManualPinPosition) => void;
  onBack: () => void;
  /**
   * Niveau de repli du centrage (#1022) — voir `AddressVerificationSection`
   * pour le threading/scope tunnel-only. Ici, seul le RENDU compte : warning
   * affiché uniquement sur les 2 paliers de repli (`"department"`/`"default"`)
   * — `"geocode"`/`"commune"` (centrage fiable) et `null` n'affichent rien.
   */
  mapCenterPrecision?: MapCenterPrecision | null;
};

// État `mapMode` (#929) : placement manuel du pin, remplace tout le rendu normal.
export function ManualPinPlacement({
  fields,
  position,
  onDragEnd,
  onBack,
  mapCenterPrecision,
}: Props) {
  const warningText = mapCenterWarningText(mapCenterPrecision);
  return (
    <div style={{marginBottom: "8px"}}>
      <div style={ADDRESS_SUMMARY_STYLE}>
        <div style={ADDRESS_SUMMARY_LINE_STYLE}>{fields.line1}</div>
        {fields.line2 && <div style={ADDRESS_SUMMARY_MUTED_LINE_STYLE}>{fields.line2}</div>}
        <div style={ADDRESS_SUMMARY_MUTED_LINE_STYLE}>
          {fields.zip} {fields.city}
        </div>
      </div>
      <button type="button" style={BACK_TO_VERIFY_LINK_STYLE} onClick={handleBackClick}>
        ← Editer le formulaire d'adresse
      </button>
      {warningText && (
        <div style={{...MAP_CENTER_WARNING_STYLE, ...RESOLVED_CARD_TINT}}>
          <span style={{...STATUS_ICON_BASE_STYLE, ...RESOLVED_ICON_TINT}}>
            <FontAwesomeIcon icon="triangle-exclamation" />
          </span>
          <span style={MAP_CENTER_WARNING_TEXT_STYLE}>{warningText}</span>
        </div>
      )}
      <MapChunkErrorBoundary>
        <Suspense
          fallback={
            <div style={{fontSize: "13px", color: "#888"}}>
              <Spinner size="sm" color="secondary" className="me-2" />
              Chargement de la carte…
            </div>
          }
        >
          <ManualPinMap
            position={position}
            onDragEnd={onDragEnd}
            hintLabel={`${fields.city} (${fields.zip})`}
          />
        </Suspense>
      </MapChunkErrorBoundary>
      <p style={{fontSize: "12px", color: "#6c757d", marginTop: "8px", marginBottom: 0}}>
        Faites glisser le repère pour indiquer l'emplacement exact.
      </p>
    </div>
  );

  function handleBackClick() {
    onBack();
  }
}

const ADDRESS_SUMMARY_STYLE: CSSProperties = {
  background: "#f6f4fb",
  borderRadius: "10px",
  padding: "10px 12px",
  fontSize: "0.88rem",
  marginBottom: "10px",
};

const ADDRESS_SUMMARY_LINE_STYLE: CSSProperties = {fontWeight: 600, color: "#333"};

const ADDRESS_SUMMARY_MUTED_LINE_STYLE: CSSProperties = {
  fontWeight: 400,
  color: "#6c757d",
  marginTop: "2px",
};

const BACK_TO_VERIFY_LINK_STYLE: CSSProperties = {
  display: "inline-block",
  fontSize: "0.8rem",
  color: "#6c757d",
  background: "none",
  border: "none",
  padding: 0,
  textDecoration: "none",
  marginBottom: "8px",
  cursor: "pointer",
};

/**
 * Texte figé (#1022, mock-up validé le 21/08) — un par palier de repli,
 * jamais un calcul de distance au pin (aucune donnée client pour ça sans un
 * vrai géocodage serveur, posture anti-oracle). `null` sur les paliers
 * fiables (`"geocode"`/`"commune"`) et en l'absence de prop.
 */
function mapCenterWarningText(precision: MapCenterPrecision | null | undefined): string | null {
  switch (precision) {
    case "department":
      return "Carte centrée approximativement, vérifiez que le repère est bien dans votre commune.";
    case "default":
      return "Nous n'avons pas pu localiser votre commune : la carte s'ouvre sur la France entière. Zoomez jusqu'à votre commune avant de placer le repère.";
    default:
      return null;
  }
}

// Callout violet conforme au design system (docs/design-system/DOCTRINE.md) — mêmes tokens que les cartes d'état "doute levé" (`RESOLVED_CARD_TINT`/`RESOLVED_ICON_TINT`), jamais un alert-warning Bootstrap jaune (#1022).
const MAP_CENTER_WARNING_STYLE: CSSProperties = {
  display: "flex",
  alignItems: "flex-start",
  gap: "8px",
  borderRadius: "10px",
  padding: "9px 11px",
  fontSize: "12px",
  lineHeight: 1.4,
  marginBottom: "10px",
};

const MAP_CENTER_WARNING_TEXT_STYLE: CSSProperties = {color: "#2c2c34", minWidth: 0};
